import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

export const parseFollowersCount = (input: string): number | null => {
  if (!input) return null;

  const cleanInput = input.toString().toLowerCase().trim();
  const numericPart = parseFloat(cleanInput);

  if (isNaN(numericPart)) return null;

  if (cleanInput.includes("m")) {
    return Math.round(numericPart * 1000000);
  } else if (cleanInput.includes("k")) {
    return Math.round(numericPart * 1000);
  } else {
    return Math.round(numericPart);
  }
};

/**
 * Links the given influencer ids to a campaign, skipping ones already assigned.
 * Returns the number of newly-linked influencers. Shows its own error toast on failure.
 */
export async function linkInfluencersToCampaign(
  campaignId: string,
  influencerIds: string[],
  queryClient: QueryClient
): Promise<number> {
  if (influencerIds.length === 0) return 0;

  const { data: alreadyAssigned, error: alreadyAssignedError } = await supabase
    .from("campaign_influencers")
    .select("influencer_id")
    .eq("campaign_id", campaignId)
    .in("influencer_id", influencerIds);

  if (alreadyAssignedError) {
    toast({
      title: "Failed to link influencers to campaign",
      description:
        alreadyAssignedError.message ||
        "Could not check existing campaign assignments.",
      variant: "destructive",
    });
    return 0;
  }

  const alreadyAssignedIds = new Set(
    (alreadyAssigned ?? []).map((row) => row.influencer_id)
  );
  const toLink = influencerIds.filter((id) => !alreadyAssignedIds.has(id));
  if (toLink.length === 0) return 0;

  const { error: linkError } = await supabase
    .from("campaign_influencers")
    .insert(
      toLink.map((influencerId) => ({
        campaign_id: campaignId,
        influencer_id: influencerId,
        status: "contacted" as const,
      }))
    );

  if (linkError) {
    toast({
      title: "Failed to link influencers to campaign",
      description:
        linkError.message ||
        "Influencers were added but could not be linked to the campaign.",
      variant: "destructive",
    });
    return 0;
  }

  await queryClient.invalidateQueries({
    queryKey: ["campaign-influencers", campaignId],
  });
  return toLink.length;
}
